<template>
    <v-app>
        <v-main class="main">

            <!-- Sidebar -->
            <AdminSidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
                @update:drawerOpen="drawerOpen = $event" />

            <v-container fluid class="main-content pa-4">

                <!-- Header -->
                <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />

                <div class="d-flex justify-space-between align-center flex-wrap ga-3 mb-4 mt-10">

                    <!-- Left -->
                    <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="$router.back()">
                        Back
                    </v-btn>

                    <!-- Right -->
                    <div class="d-flex flex-wrap ga-2 justify-end w-100 w-sm-auto">

                        <!-- Activate / Deactivate -->
                        <v-btn :color="product?.is_active ? 'red' : 'green'" variant="flat" :loading="loadingStatus"
                            @click="toggleProductStatus">
                            {{ product?.is_active ? 'Deactivate Product' : 'Activate Product' }}
                        </v-btn>

                        <!-- Dynamic Button -->
                        <v-btn :color="paymentCompleted ? 'grey' : (productCycleEnded ? 'orange' : 'black')"
                            variant="flat" :loading="loadingCycle || loadingPayment" :disabled="paymentCompleted"
                            @click="paymentCompleted ? null : (productCycleEnded ? processBulkPayment() : endProductCycle())">
                            <span v-if="paymentCompleted">
                                Payment Completed
                            </span>

                            <span v-else>
                                {{ productCycleEnded ? 'Process Payment' : 'End Product Cycle' }}
                            </span>
                        </v-btn>

                    </div>

                </div>

                <!-- Loading -->
                <div v-if="loading" class="loader">
                    <v-progress-circular indeterminate color="primary" />
                </div>

                <!-- Product Card -->
                <v-card v-else-if="product" class="product-card pa-6">

                    <!-- Top Section -->
                    <v-row>

                        <!-- Product Image -->
                        <v-col cols="12" md="3">
                            <img :src="imageUrl" class="product-detail-image" />
                            <div class="status-section mt-3">
                                <span :class="[
                                    'status-pill',
                                    product.is_active ? 'status-active' : 'status-inactive'
                                ]">
                                    {{ product.is_active ? "Active" : "Inactive" }}
                                </span>
                            </div>
                        </v-col>

                        <!-- Product Info -->
                        <v-col cols="12" md="9">

                            <div class="product-title">
                                {{ product.product_name }}
                            </div>

                            <div class="product-description">
                                <!-- {{ product.description }} -->
                                <div class="product-desc" v-html="product.description"></div>
                            </div>



                        </v-col>

                    </v-row>

                    <v-divider class="my-6" />

                    <!-- Product Details -->
                    <v-row>

                        <v-col cols="12" md="4">
                            <div class="detail-label">Maturity Date</div>
                            <div class="detail-value">
                                {{ formatDate(product.maturity_date) }}
                            </div>
                        </v-col>

                        <v-col cols="12" md="4">
                            <div class="detail-label">Available Volume</div>
                            <div class="detail-value">
                                {{ formatNumber(product.available_volume) }}
                            </div>
                        </v-col>

                        <v-col cols="12" md="4">
                            <div class="detail-label">Purchased Volume</div>
                            <div class="detail-value">
                                {{ formatNumber(product.purchased_volume) }}
                            </div>
                        </v-col>

                        <v-col cols="12" md="4">
                            <div class="detail-label">Total Volume</div>
                            <div class="detail-value">
                                {{ Number(product.purchased_volume || 0) + Number(product.available_volume || 0) }}
                            </div>
                        </v-col>

                        <v-col cols="12" md="4">
                            <div class="detail-label">Balance Withdrawn</div>
                            <div class="detail-value">
                                {{ formatCurrency(product.balance_withdrawn) }}
                            </div>
                        </v-col>

                        <v-col cols="12" md="4">
                            <div class="detail-label">Interest</div>
                            <div class="detail-value">
                                {{ parseFloat(product.interest_rate) }}%
                            </div>
                        </v-col>

                        <v-col cols="12" md="4">
                            <div class="detail-label">Minimum Tenure</div>
                            <div class="detail-value">
                                {{ (product.minimum_tenure) }} days
                            </div>
                        </v-col>

                        <v-col cols="12" md="4">
                            <div class="detail-label">Minimum Amount</div>
                            <div class="detail-value">
                                {{ formatCurrency(product.minimum_amount) }}
                            </div>
                        </v-col>

                        <v-col cols="12" md="4">
                            <div class="detail-label">Created</div>
                            <div class="detail-value">
                                {{ formatDate(product.created_at) }}
                            </div>
                        </v-col>
                        <!-- <v-col cols="12" md="12">
                            <div class="mt-6">

                                <button type="submit" :disabled="loading" class="withdraw-button"
                                    @click="withdrawDial = true">
                                    <span v-if="!loading">Edit Details <v-icon
                                            start>mdi-square-edit-outline</v-icon></span>
                                    <span v-else>Processing.… <v-icon start>mdi-loading</v-icon></span>
                                </button>

                            </div>
                        </v-col> -->
                    </v-row>

                </v-card>
                <!-- Withdraw Modal -->
                <v-dialog v-model="withdrawDialog" max-width="500">
                    <v-card class="pa-6">

                        <v-card-title class="text-h6">
                            Withdraw Funds
                        </v-card-title>

                        <v-card-text>

                            <!-- Amount Input -->
                            <v-text-field v-model="withdrawAmount" label="Enter Amount" type="number" variant="outlined"
                                density="comfortable" :error-messages="withdrawError" />

                        </v-card-text>

                        <v-card-actions>
                            <v-spacer />

                            <v-btn variant="text" @click="withdrawDialog = false">
                                Cancel
                            </v-btn>

                            <v-btn color="black" :loading="withdrawLoading" @click="handleWithdraw">
                                Confirm
                            </v-btn>

                        </v-card-actions>

                    </v-card>
                </v-dialog>

            </v-container>
            <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top right"> {{
                snackbar.message }}
                <template #actions>
                    <v-btn variant="text" @click="snackbar.show = false">
                        Close
                    </v-btn>
                </template>
            </v-snackbar>
        </v-main>
    </v-app>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue"
import { useRoute } from "vue-router"
import Header from "@/components/Header.vue"
import AdminSidebar from "@/components/AdminSidebar.vue"
import { useProductStore } from "@/store/product"
import { STORAGE_URL } from "@/api/env"
import { formatNumber, formatDate, formatCurrency } from "@/util"
import { activateProduct, deactivateProduct, endProductLifeCycle, processPayment } from "@/api/product"
import avatarImage from "@/assets/icon.png"


const route = useRoute()
const prodStore = useProductStore()

/* Layout State */

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const loading = ref(true)
const productId = route.params.id

const withdrawDialog = ref(false)
const withdrawAmount = ref("")
const withdrawLoading = ref(false)
const withdrawError = ref("")
const loadingStatus = ref(false)
const loadingCycle = ref(false)
const loadingPayment = ref(false)
const paymentCompleted = ref(false)
// const paymentCompleted = ref(false)
// const productCycleEnded = ref(false)

const defaultImage = avatarImage

const snackbar = ref({
    show: false,
    message: "",
    color: "success" // success | error | warning | info
})

const showSnackbar = (message, color = "success") => {
    snackbar.value.message = message
    snackbar.value.color = color
    snackbar.value.show = true
}


/* Sidebar Toggle */

const toggleSidebar = () => {
    if (isMobile.value) {
        drawerOpen.value = !drawerOpen.value
    } else {
        isSidebarCollapsed.value = !isSidebarCollapsed.value
    }
}

/* Mobile Check */

const checkMobile = () => {
    isMobile.value = window.innerWidth < 768
    drawerOpen.value = !isMobile.value
}

/* Product */

const product = computed(() => prodStore.singleProduct)

const productCycleEnded = computed(() => {
  return product.value?.cycle_status === "cancelled"
})

// const paymentCompleted = computed(() => {
//   return product.value?.cycle_status !== "running"
// })

/* Product Image */

const imageUrl = computed(() => {
    if (!product.value?.product_image) return defaultImage
    return `${STORAGE_URL}/${product.value.product_image}`
})

/* Load Product */

onMounted(async () => {

    checkMobile()
    window.addEventListener("resize", checkMobile)
    try {
        await prodStore.loadSingleProduct(productId)
        console.log(product.value)
    } catch (error) {
        console.error("Failed to load product:", error)
    } finally {
        loading.value = false
    }
//     productCycleEnded.value = product.value?.cycle_status === "completed"
//   paymentCompleted.value = product.value?.payment_status === true
})

onUnmounted(() => {
    window.removeEventListener("resize", checkMobile)
})

const toggleProductStatus = async () => {
    loadingStatus.value = true

    try {
        if (product.value.is_active) {
            await deactivateProduct(productId)
            product.value.is_active = false
        } else {
            await activateProduct(product.value.uuid)
            product.value.is_active = true
        }

        showSnackbar("Status updated successfully", "success")

    } catch (err) {
        showSnackbar("Failed to update status", "error")
    } finally {
        loadingStatus.value = false
    }
}

const endProductCycle = async () => {
    loadingCycle.value = true

    try {
        await endProductLifeCycle(productId)

        // 🔥 refresh product data
        await prodStore.loadSingleProduct(productId)
        showSnackbar("Product cycle ended successfully", "success")

    } catch (err) {
        console.error(err)
        showSnackbar("Failed to end product cycle", "error")

    } finally {
        loadingCycle.value = false
    }
}

const processBulkPayment = async () => {
  loadingPayment.value = true

  try {
    await processPayment(productId)

    // reload updated backend state
    paymentCompleted.value = true

    await prodStore.loadSingleProduct(productId)

    showSnackbar("Payment processed successfully", "success")

  } catch (err) {
    console.error(err)
    showSnackbar("Payment processing failed", "error")

  } finally {
    loadingPayment.value = false
  }
}
</script>

<style scoped>
.main {
    display: flex;
    min-height: 100vh;
}

.main-content {
    flex: 1;
    background: #f8fafc;
}

/* Loader */

.loader {
    display: flex;
    justify-content: center;
    margin-top: 80px;
}

/* Product Card */

.product-card {
    border-radius: 14px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

.product-desc {
    text-align: left;
    width: 1100px;
}

.product-desc * {
    text-align: left !important;
}

/* Image */

.product-detail-image {
    width: 100%;
    max-width: 200px;
    border-radius: 12px;
}

/* Title */

.product-title {
    font-size: 26px;
    font-weight: 700;
}

.product-description {
    margin-top: 10px;
    color: #64748b;
    line-height: 1.6;
}

/* Status */

.status-pill {
    padding: 5px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
}

.status-active {
    background: #ecfdf5;
    color: #16a34a;
}

.status-inactive {
    background: #fef2f2;
    color: #dc2626;
}

/* Details */

.detail-label {
    font-size: 13px;
    color: #64748b;
}

.detail-value {
    font-size: 18px;
    font-weight: 600;
    margin-top: 4px;
}

/* Button */
.withdraw-button {
    width: 30%;
    height: 46px;
    background: #000;
    color: #fff;
    border-radius: 10px;
    font-weight: 600;
    transition: all 0.25s ease;
}

.withdraw-button:hover {
    background: #292828;
}

.withdraw-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Mobile */
@media (max-width: 767px) {
    .withdraw-button {
        width: 50%;
        height: 46px;
        background: #000;
        color: #fff;
        border-radius: 10px;
        font-weight: 600;
        transition: all 0.25s ease;
    }
}
</style>
